<template>
	<div>
		<SectionCard title="Soglia OSS" icon="mdi-flag-checkered" :subtitle="`vendite a distanza B2C verso altri Paesi UE · limite ${formatEurCents(t.limit)}`" data-tour="threshold">
			<v-alert v-if="t.mustRegister" dense text type="error" class="mb-3">
				Soglia superata {{ t.exceededPrevious ? `nel ${t.year - 1}` : `a ${monthName(t.exceededMonth)}` }}: da {{ monthName(t.mandatoryFrom) }} le vendite B2C UE vanno dichiarate con l’IVA del Paese
				del cliente.
				{{ t.registered ? `L’adesione indicata parte da ${monthName(t.registered)}, dopo l’obbligo.` : 'Indica nelle impostazioni il mese di adesione all’OSS.' }}
			</v-alert>
			<v-alert v-else-if="t.registered" dense text type="success" class="mb-3">Adesione all’OSS da {{ monthName(t.registered) }}: da quel mese il quadro OSS è quello da dichiarare.</v-alert>
			<v-alert v-else-if="t.pct >= 80" dense text type="warning" class="mb-3">Mancano {{ formatEurCents(t.remaining) }} alla soglia: oltre, l’IVA diventa quella del Paese del cliente.</v-alert>
			<div class="fo-threshold">
				<div v-for="row in thresholdRows" :key="row.label" class="fo-threshold-row">
					<div class="fo-muted">{{ row.label }}</div>
					<v-progress-linear
						:value="Math.min((row.value / t.limit) * 100, 100)"
						:color="row.value > t.limit ? 'red darken-1' : row.value > t.limit * 0.8 ? 'orange darken-2' : 'primary'"
						height="8"
						rounded
					/>
					<div class="fo-strong text-right">{{ formatEurCents(row.value) }}</div>
				</div>
			</div>
			<div class="fo-muted mt-2">Imponibile con l’IVA applicata dagli ordini, resi compresi. Conta l’anno in corso o il precedente, il più alto dei due.</div>
		</SectionCard>

		<v-row dense class="mt-2" data-tour="kpi">
			<v-col v-for="k in kpis" :key="k.key" cols="12" sm="6" lg="3">
				<v-card outlined class="fo-kpi" :class="{ 'fo-kpi--link': k.tab }" @click="k.tab && $emit('tab', k.tab)">
					<div class="fo-muted">{{ k.label }}</div>
					<div class="fo-big">{{ k.value }}</div>
					<div class="fo-muted">{{ k.sub }}</div>
				</v-card>
			</v-col>
		</v-row>

		<v-alert v-if="report.summary.exports.itVatCharged > 0" dense text type="info" class="mt-2 mb-0">
			Sugli ordini extra-UE i canali hanno applicato {{ formatEurCents(report.summary.exports.itVatCharged) }} di IVA italiana: le esportazioni sono non imponibili (art. 8).
			{{ report.settings.applyToReport ? 'Nel report finanziario sono già a 0% non imponibile.' : 'Nel report finanziario restano con l’IVA italiana (impostazione disattivata).' }}
		</v-alert>
		<v-alert v-if="issuesTotal" dense text type="warning" class="mt-2 mb-0">
			{{ formatNumber(issuesTotal) }} controlli da guardare prima della dichiarazione.
			<a href="#" @click.prevent="$emit('tab', 'checks')">Vedi i controlli</a>
		</v-alert>
	</div>
</template>

<script>
import SectionCard from '@/components/core/SectionCard.vue';
import { formatEurCents, formatNumber, monthLabel } from '@/components/finance/format';

export default {
	name: 'ForeignOverview',
	components: { SectionCard },
	props: {
		report: { type: Object, required: true },
	},
	computed: {
		t() {
			return this.report.threshold;
		},
		thresholdRows() {
			return [
				{ label: `${this.t.year}`, value: this.t.current },
				{ label: `${this.t.year - 1}`, value: this.t.previous },
			];
		},
		// le aliquote ridotte sono righe da confermare, non ordini da sistemare
		issuesTotal() {
			return Object.entries(this.report.issueCount || {})
				.filter(([type]) => type !== 'reducedRate')
				.reduce((t, [, n]) => t + n, 0);
		},
		kpis() {
			const s = this.report.summary;
			const usOver = (this.report.nexus.states || []).filter(x => x.status === 'exceeded' || x.status === 'near').length;
			return [
				{
					key: 'oss',
					label: this.report.period.ossActive ? 'IVA OSS da versare' : 'IVA OSS (se aderissi)',
					value: formatEurCents(s.oss.vat + (s.corrections.vat || 0)),
					sub: `${formatNumber(s.oss.orders)} ordini · ${s.oss.diff >= 0 ? '+' : ''}${formatEurCents(s.oss.diff)} rispetto all’IVA italiana`,
					tab: 'oss',
				},
				{ key: 'b2b', label: 'Cessioni B2B UE', value: formatEurCents(s.intraB2b.net), sub: `${formatNumber(s.intraB2b.orders)} ordini non imponibili (art. 41)`, tab: 'b2b' },
				{ key: 'exports', label: 'Esportazioni extra-UE', value: formatEurCents(s.exports.net), sub: `${formatNumber(s.exports.orders)} ordini non imponibili (art. 8)`, tab: 'exports' },
				{ key: 'us', label: 'Stati USA da guardare', value: formatNumber(usOver), sub: 'vicini o oltre la soglia di sales tax', tab: 'us' },
			];
		},
	},
	methods: {
		formatEurCents,
		formatNumber,
		monthName(month) {
			return month ? monthLabel(month).toLowerCase() : '';
		},
	},
};
</script>

<style scoped>
.fo-threshold {
	display: grid;
	gap: 8px;
}
.fo-threshold-row {
	display: grid;
	grid-template-columns: 60px 1fr 120px;
	align-items: center;
	gap: 12px;
}
.fo-kpi {
	padding: 12px 14px;
	border-radius: 10px !important;
	height: 100%;
}
.fo-kpi--link {
	cursor: pointer;
}
.fo-big {
	font-size: 20px;
	font-weight: 600;
	margin: 2px 0;
}
.fo-strong {
	font-weight: 600;
}
.fo-muted {
	font-size: 12px;
	color: #5f6b7a;
}
</style>
